import type {InsightTargetPeriodV4, InsightTimeSeriesRangeV4} from "../../models/crm-v4";
import type {InsightTargetMetricDefinition} from "./targetDefinitions";

export interface InsightTargetRange {
    from: string;
    to: string;
}

export interface ResolvedInsightCardTarget {
    value: number;
    progress: number;
    achieved: boolean;
}

const ISO_LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;

/** Parses a calendar date without allowing browser timezone or DST rules to change its day count. */
function localDateOrdinal(value: string): number | undefined {
    const match = ISO_LOCAL_DATE.exec(value);
    if (!match) return undefined;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const timestamp = Date.UTC(year, month - 1, day);
    const parsed = new Date(timestamp);

    if (
        parsed.getUTCFullYear() !== year
        || parsed.getUTCMonth() !== month - 1
        || parsed.getUTCDate() !== day
    ) return undefined;

    return timestamp / DAY_MILLISECONDS;
}

function inclusiveDays(range: InsightTargetRange): number | undefined {
    const from = localDateOrdinal(range.from);
    const to = localDateOrdinal(range.to);
    if (from == null || to == null || to < from) return undefined;
    return to - from + 1;
}

function intersectRanges(
    left: InsightTargetRange,
    right: InsightTargetRange,
): InsightTargetRange | undefined {
    const leftFrom = localDateOrdinal(left.from);
    const leftTo = localDateOrdinal(left.to);
    const rightFrom = localDateOrdinal(right.from);
    const rightTo = localDateOrdinal(right.to);
    if (
        leftFrom == null
        || leftTo == null
        || rightFrom == null
        || rightTo == null
        || leftTo < leftFrom
        || rightTo < rightFrom
    ) return undefined;

    const from = Math.max(leftFrom, rightFrom);
    const to = Math.min(leftTo, rightTo);
    if (to < from) return undefined;

    return {
        from: leftFrom >= rightFrom ? left.from : right.from,
        to: leftTo <= rightTo ? left.to : right.to,
    };
}

export function activeTargetPeriod(periods: InsightTargetPeriodV4[] | undefined): InsightTargetPeriodV4 | undefined {
    return periods?.find(period => period.active);
}

export function hasTargetValues(period: InsightTargetPeriodV4 | undefined): boolean {
    return Object.values(period?.targets ?? {}).some(value => Number.isFinite(value) && value > 0);
}

/** Resolves one stored target for the part of the requested range that overlaps its active period. */
export function resolveTarget(
    period: InsightTargetPeriodV4 | undefined,
    metric: InsightTargetMetricDefinition,
    range: InsightTargetRange,
): number | undefined {
    if (!period?.active) return undefined;
    const target = period.targets[metric.id];
    if (!Number.isFinite(target) || target <= 0) return undefined;

    const overlap = intersectRanges({from: period.from, to: period.to}, range);
    if (!overlap) return undefined;

    if (metric.kind === "RATE") return target;
    const periodDays = inclusiveDays({from: period.from, to: period.to});
    const overlapDays = inclusiveDays(overlap);
    if (periodDays == null || overlapDays == null) return undefined;
    return target * overlapDays / periodDays;
}

export function resolveCardTarget(
    period: InsightTargetPeriodV4 | undefined,
    metric: InsightTargetMetricDefinition,
    range: InsightTargetRange,
    actual: number | null | undefined,
): ResolvedInsightCardTarget | undefined {
    const value = resolveTarget(period, metric, range);
    if (actual == null || !Number.isFinite(actual) || value == null || value <= 0) return undefined;

    return {
        value,
        progress: actual / value * 100,
        achieved: metric.scoring === "LOWER_IS_BETTER" ? actual <= value : actual >= value,
    };
}

/** Returns one target value per chart bucket; cumulative metrics become a running target total. */
export function resolveChartTargets(
    period: InsightTargetPeriodV4 | undefined,
    metric: InsightTargetMetricDefinition,
    selectedRange: InsightTargetRange,
    points: InsightTimeSeriesRangeV4[],
): Array<number | null> | undefined {
    if (resolveTarget(period, metric, selectedRange) == null) return undefined;
    const parts = points.map(point => resolveTarget(period, metric, point));
    if (metric.kind === "RATE") return parts.map(value => value ?? null);

    let running = 0;
    return parts.map(value => {
        if (value == null) return null;
        running += value;
        return running;
    });
}
